// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable hydrogen/prefer-image-component */
import {
  useCart,
  useCartLine,
  CartLineQuantity,
  Image,
  Link,
  Money,
} from '@shopify/hydrogen';
import type {
  AttributeInput,
  CartLine,
  Image as ImageType,
  InputMaybe,
  MoneyV2,
} from '@shopify/hydrogen/storefront-api-types';
import {CurrencyCode} from '@shopify/hydrogen/storefront-api-types';

import {IconMinus, IconPlus, Text} from '~/components';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {CUSTOM_ATTRIBUTES_KEYS, DATA_TEST_IDS} from '~/lib/const';
import {isDiscounted} from '~/lib/utils';
import iconCheck from '../../assets/icons/icon-check.svg';
import clsx from 'clsx';

function findAddonLineItem(lineItems: CartLine[], lineItem: CartLine) {
  const {attributes} = lineItem;
  const parentProductVariantId = attributes.find(({key}) => {
    return key === CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_VARIANT_ID;
  })?.value;
  const parentProduct = lineItems.find((lineItem) => {
    return lineItem.merchandise.id === parentProductVariantId;
  });
  return parentProduct;
}

function getLinkedProducts(
  lineItems: CartLine[],
  lineItem: CartLine,
  bundleHash?: string,
) {
  const {merchandise} = lineItem;
  const variantId = merchandise.id;
  const parentProduct = findAddonLineItem(lineItems, lineItem);

  const childrenProduct = lineItems.filter((lineItem) => {
    const parentProductVariantId = lineItem.attributes.find(({key}) => {
      return key === CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_VARIANT_ID;
    })?.value;

    const parentProductTitle = lineItem.attributes.find(({key}) => {
      return key === 'For';
    })?.value;

    const isBundlePart =
      bundleHash &&
      lineItem.attributes.find(({key}) => {
        return key === '_bundle_hash';
      })?.value === bundleHash;

    return (
      (parentProductVariantId != null &&
        parentProductVariantId === variantId) ||
      (parentProductTitle != null &&
        parentProductTitle == merchandise.product.title) ||
      isBundlePart
    );
  });

  return {
    parentProduct,
    childrenProduct,
  };
}

export function CartLineItem({
  quantities,
  addonLines,
}: {
  quantities: Record<string, number>;
  addonLines: any[];
}) {
  const {linesAdd, linesUpdate, linesRemove, lines} = useCart();
  const lineItem = useCartLine();
  const {id: lineId, quantity, cost, merchandise} = lineItem;

  const [loadingAddons, setLoadingAddons] = useState<boolean>(false);
  const [loadingQuantities, setLoadingQuantities] = useState<boolean>(false);

  const [baseAddonSelectedIndexes, setBaseAddonSelectedIndexes] = useState<
    number[][]
  >([]);

  const isBOPISOnly = useMemo(() => {
    return !!lineItem.attributes.find(
      (attr) =>
        attr.key === CUSTOM_ATTRIBUTES_KEYS.BOPIS_ONLY && attr.value === 'true',
    );
  }, [lineItem.attributes]);

  const auto = lineItem.attributes.find(({key}) => {
    return key === '_auto';
  })?.value;

  const merged = lineItem.attributes.find(({key}) => {
    return key === '_merged';
  })?.value;

  const bundleID = useMemo(() => {
    return lineItem.attributes.find(
      (attr) => attr.key === '_bundle', //CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_BUNDLE,
    )?.value;
  }, [lineItem.attributes]);

  const bundleHash = useMemo(() => {
    return lineItem.attributes.find((attr) => attr.key === '_bundle_hash')
      ?.value;
  }, [lineItem.attributes]);

  // @ts-ignore
  const linkedProducts = getLinkedProducts(lines, lineItem, bundleHash);
  const handleRemove = useCallback(() => {
    const itemsToRemove = [lineId];
    const {childrenProduct} = linkedProducts;

    if (childrenProduct.length > 0) {
      for (const childProduct of childrenProduct) {
        if (itemsToRemove.indexOf(childProduct.id) < 0)
          itemsToRemove.push(childProduct.id);
      }
    }

    linesRemove(itemsToRemove);
  }, [lineId, linkedProducts, linesRemove]);

  const lineAddons = useMemo(() => {
    let childAddonsIds = lineItem.attributes.filter((attr) =>
      attr.key.startsWith(CUSTOM_ATTRIBUTES_KEYS.ADDON_PRODUCT_VARIANT_ID),
    );

    if (childAddonsIds.length == 0 && addonLines?.length > 0) {
      addonLines.forEach((addon) => {
        addon.attributes.forEach((attr) => {
          if (
            attr.key == '_ADDON_PARENT_PRODUCT_TITLE' &&
            attr.value == lineItem.merchandise.product.title
          ) {
            childAddonsIds = [addon.merchandise.id];
          }
        });
      });
    }

    if (childAddonsIds) {
      return childAddonsIds.map((childAddonId) => {
        const id = childAddonId.key?.replace(
          `${CUSTOM_ATTRIBUTES_KEYS.ADDON_PRODUCT_VARIANT_ID}-`,
          '',
        );

        if (!id) {
          const addonLine = lines.find((line) =>
            line.merchandise.id.endsWith(childAddonId),
          );

          return {
            id: addonLine?.id,
            merchandiseId: childAddonId.value ?? '',
            quantity: addonLine?.quantity ?? 0,
            title: addonLine?.merchandise?.product.title ?? '',
            price: addonLine?.merchandise?.priceV2.amount ?? '0',
            attributes: [
              {
                key: CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_VARIANT_ID,
                value: String(merchandise.id),
              },
              {
                key: CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_TITLE,
                value: merchandise.title,
              },
            ],
          };
        }

        const addonLine = lines.find((line) =>
          line.merchandise.id.endsWith(id),
        );

        const title = lineItem.attributes.find(
          (attr) =>
            attr.key === `${CUSTOM_ATTRIBUTES_KEYS.ADDON_PRODUCT_NAME}-${id}`,
        );

        const price = lineItem.attributes.find(
          (attr) =>
            attr.key === `${CUSTOM_ATTRIBUTES_KEYS.ADDON_PRODUCT_PRICE}-${id}`,
        );

        return {
          id: addonLine?.id,
          merchandiseId: childAddonId.value ?? '',
          quantity: addonLine?.quantity ?? 0,
          title: title?.value ?? '',
          price: price?.value ?? '0',
          attributes: [
            {
              key: CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_VARIANT_ID,
              value: String(merchandise.id),
            },
            {
              key: CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_TITLE,
              value: merchandise.title,
            },
          ],
        };
      });
    }

    return undefined;
  }, [lineItem.attributes, lines, merchandise]);

  let bundleCost = 0;
  for (const line of lines) {
    if (
      line.attributes.find(
        (attr) => attr.key === '_bundle_hash' && attr.value === bundleHash,
      )
    ) {
      bundleCost += parseFloat(line.cost.totalAmount.amount);
    }
  }

  const linesBundle = useMemo(() => {
    const lineBundleItems: typeof lines = [];
    const types: string[] = [];

    for (const line of lines) {
      if (
        line.attributes.find(
          (attr) => attr.key === '_bundle_hash' && attr.value === bundleHash,
        ) &&
        !line.attributes.find((attr) => attr.key === '_bundle_main')
      ) {
        const type =
          line.attributes.find(
            (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
          )?.value ?? '';

        if (types.includes(type)) {
          continue;
        }

        lineBundleItems.push(line);
        types.push(type);
      }
    }

    return lineBundleItems;
  }, [bundleHash, lines]);

  const maxQuantities = useMemo(() => {
    const maxQuantities: Record<string, number> = {};

    if (linesBundle.length > 0) {
      for (const line of linesBundle) {
        const quantity = line.attributes.find(
          (attr) =>
            attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
        )?.value;

        if (line && !isNaN(Number(quantity))) {
          maxQuantities[line.merchandise.id] = Number(quantity);
        }
      }
    } else {
      const quantity = lineItem.attributes.find(
        (attr) => attr.key === CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
      )?.value;

      maxQuantities[lineItem.merchandise.id] = Number(quantity);
    }

    return maxQuantities;
  }, [lineItem, linesBundle]);

  const maxQuantityReached = useMemo(() => {
    if (Object.keys(maxQuantities).length === 0) {
      return false;
    }

    const ids = [
      lineItem.merchandise.id,
      ...linesBundle.map((line) => line.merchandise.id),
    ];

    const bundleQuantities = Object.keys(quantities)
      .filter((key) => ids.includes(key))
      .map((key) => quantities[key] >= maxQuantities[key]);

    return bundleQuantities.includes(true);
  }, [lineItem.merchandise.id, linesBundle, maxQuantities, quantities]);

  const handleAddAddon = useCallback(
    (itemEnabled: boolean, index: number, indexAddon: number) => {
      if (!lineAddons) {
        return;
      }

      setLoadingAddons(true);

      if (itemEnabled) {
        setBaseAddonSelectedIndexes((prev) =>
          prev.map((a, i) =>
            i === indexAddon ? a.filter((i) => i !== index) : a,
          ),
        );
      } else {
        setBaseAddonSelectedIndexes((prev) =>
          prev.map((a, i) => (i === indexAddon ? [...a, index] : a)),
        );
      }

      const lineAddon = lineAddons[indexAddon];

      // If the line item is not in the cart, add it
      if (lineAddon.quantity === 0) {
        linesAdd([
          {
            attributes: lineAddon.attributes as InputMaybe<AttributeInput[]>,
            merchandiseId: lineAddon.merchandiseId,
            quantity: 1,
          },
        ]);
      } else if (itemEnabled && lineAddon.id && lineAddon.quantity === 1) {
        // If the line item is in the cart and the quantity is 1, remove it
        linesRemove([lineAddon.id]);
      } else {
        // If the line item is in the cart and the quantity is more than 1, update it
        linesUpdate([
          {
            id: lineAddon.id!,
            attributes: lineAddon.attributes as InputMaybe<AttributeInput[]>,
            merchandiseId: lineAddon.merchandiseId,
            quantity: itemEnabled
              ? lineAddon.quantity - 1
              : lineAddon.quantity + 1,
          },
        ]);
      }

      setTimeout(() => {
        setLoadingAddons(false);
      }, 2000);
    },
    [lineAddons, linesAdd, linesRemove, linesUpdate],
  );

  const handleQuantityChange = useCallback(
    (type: 'increase' | 'decrease') => {
      setLoadingQuantities(true);

      const itemsToUpdate = [
        {
          id: lineItem.id,
          attributes: lineItem.attributes as InputMaybe<AttributeInput[]>,
          merchandiseId: lineItem.merchandise.id,
          quantity:
            type === 'decrease' ? lineItem.quantity - 1 : lineItem.quantity + 1,
        },
      ];

      for (const lineBundle of linesBundle) {
        itemsToUpdate.push({
          id: lineBundle.id,
          attributes: lineBundle.attributes as InputMaybe<AttributeInput[]>,
          merchandiseId: lineBundle.merchandise.id,
          quantity:
            type === 'decrease'
              ? lineBundle.quantity - 1
              : lineBundle.quantity + 1,
        });
      }

      // If the line has a addon, we need to update it too
      // But only if the quantity is the same as the parent line and the addon is being decreased
      // (if the parent line is 2 and the addon is 2, we want to update the addon)
      // (if the parent line is 2 and the addon is 1, we don't want to update the addon)
      for (const lineAddon of lineAddons ?? []) {
        if (type === 'decrease' && lineAddon?.quantity === lineItem.quantity) {
          itemsToUpdate.push({
            id: lineAddon.id!,
            attributes: lineAddon.attributes as InputMaybe<AttributeInput[]>,
            merchandiseId: lineAddon.merchandiseId,
            quantity: lineAddon.quantity - 1,
          });
        }
      }

      linesUpdate(itemsToUpdate);

      setTimeout(() => {
        setLoadingQuantities(false);
      }, 2000);
    },
    [linesUpdate, lineItem, linesBundle, lineAddons],
  );

  const selectedOptions = useMemo(() => {
    const options: Array<{
      name: string;
      value: string;
    }> = [];

    if (bundleID) {
      for (const attributes of lineItem.attributes) {
        if (
          ![
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_ID,
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_MAIN,
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_BUNDLE,
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_PRICE,
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TOTAL_PRICE,
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
            '_bundle_hash',
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_BOOT_SIZE,
            CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_POLE_SIZE,
          ].includes(attributes.key) &&
          !attributes.key.startsWith('_')
        ) {
          options.push({
            name: attributes.key,
            value: attributes.value ?? '',
          });
        }
      }

      for (const item of linesBundle) {
        for (const option of item.merchandise.selectedOptions) {
          if (option.name.toLowerCase() !== 'title') {
            options.push({
              name: option.name,
              value: option.value,
            });
          }
        }
      }
    } else if (merchandise.selectedOptions) {
      merchandise.selectedOptions.forEach((option) => {
        options.push({
          name: option.name,
          value: option.value,
        });
      });
    }

    for (const attributes of lineItem.attributes) {
      if (attributes.key.startsWith('_option_')) {
        options.push({
          name: attributes.key.replace('_option_', ''),
          value: attributes.value ?? '',
        });
      }
    }

    return options;
  }, [bundleID, merchandise.selectedOptions, lineItem.attributes, linesBundle]);

  useEffect(() => {
    if (lineAddons && baseAddonSelectedIndexes.length === 0) {
      setBaseAddonSelectedIndexes(
        Array.from(Array(lineAddons.length).keys()).map((_, i) =>
          Array.from(Array(lineAddons[i].quantity).keys()),
        ),
      );
    }
  }, [lineAddons, baseAddonSelectedIndexes.length, quantity]);

  const price = useMemo(() => merchandise.priceV2, [merchandise.priceV2]);
  const itemCost = useMemo(() => cost.totalAmount, [cost.totalAmount]);
  const compareAtPriceV2 = useMemo(
    () => merchandise.compareAtPriceV2 as MoneyV2,
    [merchandise.compareAtPriceV2],
  );

  const discounted =
    (compareAtPriceV2 && isDiscounted(price as MoneyV2, compareAtPriceV2)) ||
    isDiscounted(itemCost as MoneyV2, price as MoneyV2);

  const linePrice = parseFloat(merchandise.priceV2.amount) * quantity;

  // const lineSubtotal = useMemo(() => {
  //   let subtotal = Number(itemCost.amount) * quantity;

  //   for (const lineAddon of lineAddons ?? []) {
  //     if (lineAddon && lineAddon.quantity > 0) {
  //       const price = Number(lineAddon.price);
  //       const quantity = Number(lineAddon.quantity);

  //       subtotal += price * quantity;
  //     }
  //   }

  //   return subtotal;
  // }, [lineAddons, price.amount, quantity]);

  return (
    <li
      data-testid={DATA_TEST_IDS.CART_LINE_ITEM}
      key={lineId}
      className="flex gap-2.5 sm:gap-[30px] border-b border-b-gray-300 pb-5"
    >
      <div className="flex-shrink-0">
        {merchandise.image && (
          <Image
            loading="lazy"
            width={132}
            height={132}
            widths={[132]}
            data={merchandise.image as ImageType}
            loaderOptions={{
              scale: 2,
              crop: 'center',
            }}
            className="object-cover object-center w-[92px] h-[92px] border rounded sm:w-[132px] sm:h-[132px]"
          />
        )}
      </div>

      <div className="flex flex-col gap-5 flex-1">
        <div className="flex justify-between flex-col sm:flex-row flex-grow items-baseline gap-1 sm:gap-11">
          <div className="grid gap-5">
            <Text as="h6" className="tracking-tight">
              <Link
                data-testid={DATA_TEST_IDS.LINK(merchandise.product.title)}
                to={`/products/${merchandise.product.handle}`}
              >
                {auto &&
                parseFloat(itemCost.amount) < 0.01 &&
                merchandise.product.title.toUpperCase() ==
                  'SKIESSENTIALS GIFT CARD'
                  ? 'FREE GIFT CARD'
                  : merchandise.product.title.toUpperCase()}
              </Link>
            </Text>

            <div className="grid gap-[5px]">
              {selectedOptions.map(
                (option) =>
                  option.name.toUpperCase().indexOf('BUNDLE') < 0 &&
                  option.value.indexOf('Default Title') < 0 && (
                    <span
                      data-testid={DATA_TEST_IDS.CART_LINE_OPTION}
                      key={option.name}
                      className="text-14/16 font-header font-bold"
                    >
                      {option.name.toUpperCase()}: {option.value.toUpperCase()}
                    </span>
                  ),
              )}
            </div>
          </div>
          <button
            data-testid={DATA_TEST_IDS.BUTTON('remove')}
            type="button"
            className="text-14/16 font-header underline self-end sm:self-auto"
            onClick={handleRemove}
          >
            REMOVE
          </button>
        </div>

        <div className="flex justify-between items-end gap-2 flex-col sm:flex-row">
          <div className="flex justify-start text-copy border-2 border-gray-100 rounded-[5px]">
            {!bundleID && !merged && (
              <CartLineQuantityAdjust
                lineId={lineId}
                quantity={quantity}
                onChange={handleQuantityChange}
                loading={loadingQuantities}
                maxQuantityReached={maxQuantityReached}
              />
            )}
          </div>

          {(!lineAddons || lineAddons.length === 0) && (
            <div className="font-header font-bold text-16/20 flex gap-5">
              {!bundleID && discounted && (
                <span className="">
                  <Money
                    data-testid={DATA_TEST_IDS.COMPARE_AT_PRICE}
                    className="text-gray-400 line-through thick"
                    data={{
                      amount: compareAtPriceV2
                        ? compareAtPriceV2.amount
                        : linePrice.toString(),
                      currencyCode: CurrencyCode.Usd,
                    }}
                  />
                </span>
              )}
              {bundleID && (
                <span className="">
                  <Money
                    data-testid={DATA_TEST_IDS.ADDON_MONEY}
                    className="font-header font-bold text-16/20"
                    data={{
                      amount:
                        bundleCost > 0
                          ? bundleCost.toString()
                          : merchandise.priceV2.amount.toString(),
                      currencyCode: CurrencyCode.Usd,
                    }}
                  />
                </span>
              )}
              {!bundleID && (
                <span>
                  <Money
                    data-testid={DATA_TEST_IDS.ADDON_MONEY}
                    className="font-header font-bold text-16/20"
                    data={itemCost}
                  />
                </span>
              )}
            </div>
          )}
        </div>

        {lineAddons?.map((lineAddon, index) => (
          <CartLineBaseAddon
            key={lineAddon.merchandiseId}
            quantity={lineItem.quantity}
            baseAddonSelectedIndexes={baseAddonSelectedIndexes[index] ?? []}
            lineAddon={lineAddon}
            onClick={(itemEnabled, i) => handleAddAddon(itemEnabled, i, index)}
            loading={loadingAddons}
          />
        ))}

        <div className="flex justify-between -mt-2.5">
          <div className="italic text-13/17">
            {isBOPISOnly && 'Only available for in store pick up'}
          </div>

          {lineAddons && lineAddons.length > 0 && (
            <div className="font-header font-bold text-16/20 flex gap-5">
              {!bundleID && discounted && (
                <span className="">
                  <Money
                    data-testid={DATA_TEST_IDS.COMPARE_AT_PRICE}
                    className="text-gray-400 line-through thick"
                    data={compareAtPriceV2 ? compareAtPriceV2 : itemCost}
                  />
                </span>
              )}
              {bundleID && (
                <span className="">
                  <Money
                    data-testid={DATA_TEST_IDS.ADDON_MONEY}
                    className="font-header font-bold text-16/20"
                    data={{
                      amount:
                        bundleCost > 0
                          ? bundleCost.toString()
                          : merchandise.priceV2.amount.toString(),
                      currencyCode: CurrencyCode.Usd,
                    }}
                  />
                </span>
              )}
              {!bundleID && (
                <span>
                  <Money
                    data-testid={DATA_TEST_IDS.ADDON_MONEY}
                    className="font-header font-bold text-16/20"
                    data={itemCost}
                  />
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function CartLineQuantityAdjust({
  lineId,
  quantity,
  maxQuantityReached,
  loading,
  onChange,
}: {
  lineId: string;
  quantity: number;
  maxQuantityReached: boolean;
  loading?: boolean;
  onChange: (type: 'increase' | 'decrease') => void;
}) {
  return (
    <>
      <label
        data-testid={DATA_TEST_IDS.CART_LINE_QUANTITY(quantity)}
        htmlFor={`quantity-${lineId}`}
        className="sr-only"
      >
        Quantity, {quantity}
      </label>
      <div className="flex items-center border rounded">
        <button
          data-testid={DATA_TEST_IDS.BUTTON('minus')}
          disabled={loading}
          type="button"
          onClick={() => onChange('decrease')}
          aria-label="Decrease quantity"
          className="w-8 h-11 transition text-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 grid place-content-center"
        >
          <IconMinus className="text-black w-[13px]" />
        </button>
        <CartLineQuantity
          as="div"
          className="px-2 text-center text-18/26 font-medium w-11"
        />
        <button
          data-testid={DATA_TEST_IDS.BUTTON('plus')}
          type="button"
          onClick={() => onChange('increase')}
          aria-label="Increase quantity"
          className="w-8 h-11 transition text-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 grid place-content-center"
          disabled={maxQuantityReached || loading}
        >
          <IconPlus className="text-black w-[13px]" />
        </button>
      </div>
    </>
  );
}

function CartLineBaseAddon({
  quantity,
  lineAddon,
  baseAddonSelectedIndexes,
  loading,
  onClick,
}: {
  quantity: number;
  baseAddonSelectedIndexes: number[];
  lineAddon?: {
    id: string | undefined;
    title: string;
    price: string;
  };
  loading: boolean;
  onClick: (itemEnabled: boolean, index: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {lineAddon && (
        <div
          data-testid={DATA_TEST_IDS.BASE_ADDON}
          className="flex flex-col gap-2.5"
        >
          {Array.from(Array(quantity)).map((_, index) => {
            const itemEnabled = baseAddonSelectedIndexes.includes(index);

            return (
              <button
                data-testid={DATA_TEST_IDS.BUTTON('enable')}
                disabled={loading}
                type="button"
                onClick={() => onClick(itemEnabled, index)}
                // eslint-disable-next-line react/no-array-index-key
                key={`${lineAddon.id}-${index}`}
                className={clsx(
                  'border rounded-[5px] px-2.5 py-2 flex gap-2.5 w-full max-w-[269px] text-left',
                  itemEnabled ? 'border-black' : 'border-gray-100',
                )}
              >
                <div
                  className={clsx(
                    'w-[30px] h-[30px] rounded-[5px] grid place-content-center flex-shrink-0',
                    itemEnabled ? 'bg-blue-500' : 'border border-gray-100',
                  )}
                >
                  {itemEnabled && (
                    <img
                      data-testid={DATA_TEST_IDS.BASE_ADDON_ENABLED}
                      loading="lazy"
                      src={iconCheck}
                      alt=""
                    />
                  )}
                </div>
                <span className="font-header text-14/16 font-bold">
                  {lineAddon.title.toUpperCase()}{' '}
                  <Money
                    as="span"
                    className="before:content-['(+'] after:content-[')']"
                    withoutTrailingZeros
                    data={{
                      amount: lineAddon.price,
                      currencyCode: CurrencyCode.Usd,
                    }}
                  />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
